import React from "react";
import { FaFileExport } from "react-icons/fa6";
import * as XLSX from "xlsx";
import { formatSalesValue } from "../../../utils/formatUtils";

const DepartmentDetailedTable = ({
    departments,
    summary,
    aggregated,
    company,
    selectedOutlet,
    startDate,
    endDate,
}) => {
    const hasData =
        departments &&
        departments.length > 0 &&
        departments.some(dept => dept.vendors?.length > 0);

    const exportToExcel = () => {
        if (!hasData) return;

        const workbook = XLSX.utils.book_new();
        const totalColumns = aggregated ? 7 : 8;

        departments.forEach((dept) => {

            const rows = [];
            const cleanDepartmentName = dept.department.trim();

            /* =========================
               HEADER SECTION
            ==========================*/

            rows.push([company]);
            rows.push([selectedOutlet || ""]);
            rows.push([
                `Detailed Purchase Report From: ${startDate.toLocaleDateString()} To: ${endDate.toLocaleDateString()}`
            ]);
            rows.push([`Department: ${cleanDepartmentName}`]);
            rows.push([]);

            /* =========================
               TABLE HEADER
            ==========================*/

            const headerRow = [
                "Vendor",
                "Item Name",
                "Brand",
                "UOM",
                "Qty",
                "Rate",
                "Total",
            ];

            if (!aggregated) {
                headerRow.push("Bill No");
            }

            rows.push(headerRow);

            /* =========================
               DATA
            ==========================*/

            dept.vendors.forEach((vendor) => {

                // Vendor title row
                rows.push([vendor.vendor]);

                const purchaseItems = vendor.items.filter(i => i.source_type === "purchase");
                const debitItems = vendor.items.filter(i => i.source_type === "debitnote");

                const purchaseTotal = purchaseItems.reduce((sum, i) => sum + i.Total, 0);
                const debitTotal = debitItems.reduce((sum, i) => sum + i.Total, 0);
                const netTotal = purchaseTotal - debitTotal;

                /* ========= PURCHASE ROWS ========= */
                purchaseItems.forEach((item) => {
                    const row = [
                        "",
                        item.Name,
                        item.BrandName || "-",
                        item.UOM,
                        item.ItemCount,
                        item.ItemRate,
                        item.Total,
                    ];

                    if (!aggregated) {
                        row.push(item.purchaseBillNumber || "-");
                    }

                    rows.push(row);
                });

                /* ========= PURCHASE TOTAL ========= */
                let purchaseRow = new Array(totalColumns).fill("");
                purchaseRow[5] = "Purchase Total";
                purchaseRow[6] = purchaseTotal;
                rows.push(purchaseRow);

                /* ========= DEBIT NOTE ROWS ========= */
                if (debitItems.length > 0) {

                    debitItems.forEach((item) => {
                        const row = [
                            "",
                            item.Name,
                            item.BrandName || "-",
                            item.UOM,
                            -item.ItemCount,   // NEGATIVE
                            item.ItemRate,
                            -item.Total,       // NEGATIVE
                        ];

                        if (!aggregated) {
                            row.push(Number(item.purchaseBillNumber) || "-");
                        }

                        rows.push(row);
                    });

                    /* ========= DEBIT TOTAL ========= */
                    let debitRow = new Array(totalColumns).fill("");
                    debitRow[5] = "Debit Total";
                    debitRow[6] = -debitTotal;
                    rows.push(debitRow);

                    /* ========= NET TOTAL ========= */
                    let netRow = new Array(totalColumns).fill("");
                    netRow[5] = "Net Total";
                    netRow[6] = netTotal;
                    rows.push(netRow);
                }

            });

            /* =========================
               CREATE SHEET
            ==========================*/

            const worksheet = XLSX.utils.aoa_to_sheet(rows);

            worksheet["!cols"] = headerRow.map((_, i) => ({
                wch: Math.max(
                    18,
                    ...rows.map(row =>
                        row[i] ? row[i].toString().length : 10
                    )
                )
            }));

            // Merge title rows
            worksheet["!merges"] = [
                { s: { r: 0, c: 0 }, e: { r: 0, c: totalColumns - 1 } },
                { s: { r: 1, c: 0 }, e: { r: 1, c: totalColumns - 1 } },
                { s: { r: 2, c: 0 }, e: { r: 2, c: totalColumns - 1 } },
                { s: { r: 3, c: 0 }, e: { r: 3, c: totalColumns - 1 } },
            ];

            XLSX.utils.book_append_sheet(
                workbook,
                worksheet,
                cleanDepartmentName.substring(0, 31)
            );
        });

        const formatDateForFile = (date) => {
            return new Date(date)
                .toISOString()
                .split("T")[0];
        };

        const sanitize = (text) => {
            return text
                .replace(/[\/\\:*?"<>|]/g, "")
                .replace(/\s+/g, "_")
                .trim();
        };

        const outletName = sanitize(selectedOutlet || "Outlet");
        const formattedStart = formatDateForFile(startDate);
        const formattedEnd = formatDateForFile(endDate);

        const fileName =
            `Department_Wise_Purchase_Report_${outletName}_${formattedStart}_to_${formattedEnd}.xlsx`;

        XLSX.writeFile(workbook, fileName);
    };

    const computedDepartmentTotals = departments.reduce((acc, dept) => {
        const deptName = dept.department.trim();

        if (!acc[deptName]) acc[deptName] = 0;

        dept.vendors.forEach(vendor => {
            vendor.items.forEach(item => {
                if (item.source_type === "purchase") {
                    acc[deptName] += item.Total;
                } else if (item.source_type === "debitnote") {
                    acc[deptName] -= item.Total;
                }
            });
        });

        return acc;
    }, {});

    if (!hasData) {
        return (
            <div className="max-w-5xl mx-auto mt-4 text-sm italic font-semibold text-center rounded-md bg-gray-50 p-4">
                No purchase data available. Select dates and click on View.
            </div>
        );
    }

    return (
        <div className="max-w-4xl mx-auto text-sm">
            <div className="flex justify-between items-start mb-5 px-10">
                <div className="flex-1 flex justify-start" />

                <div className="flex-1 flex justify-center">
                    {computedDepartmentTotals && (
                        <div className="p-3 w-[400px] rounded-xl border border-gray-200 shadow-sm bg-gray-50 text-sm">
                            <div className="grid grid-cols-2 gap-y-3">
                                {Object.entries(computedDepartmentTotals).map(([deptName, total], index) => (
                                    <React.Fragment key={index}>
                                        <div className="text-gray-600">
                                            {deptName} Total
                                        </div>
                                        <div className="text-right font-medium">
                                            {formatSalesValue(total)}
                                        </div>
                                    </React.Fragment>
                                ))}

                                <div className="font-semibold text-base border-t pt-2">
                                    Overall Total
                                </div>
                                <div className="text-right font-bold text-base border-t pt-2">
                                    {formatSalesValue(
                                        Object.values(computedDepartmentTotals).reduce((a, b) => a + b, 0)
                                    )}
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                <div className="flex-1 flex justify-end">
                    <button
                        onClick={exportToExcel}
                        className="flex items-center gap-2 px-3 py-2 text-sm rounded-sm font-medium bg-blue-100 text-blue-800 hover:bg-blue-200 hover:shadow-md"
                    >
                        <FaFileExport className="h-3 w-3" />
                        Export
                    </button>
                </div>
            </div>

            <h1 className="text-3xl text-center underline font-bold">
                {company}
            </h1>

            <h1 className="text-lg md:text-xl text-center font-medium">
                {selectedOutlet}
            </h1>

            <p className="text-center underline font-semibold">
                Detailed Purchase Report From: {startDate.toLocaleDateString()} To: {endDate.toLocaleDateString()}
            </p>

            {departments.map((dept, deptIndex) => {
                const cleanDepartmentName = dept.department.trim();

                return (
                    <div key={deptIndex} className="mb-12">

                        {/* Department Title */}
                        <h2 className="text-xl font-bold text-center underline mb-4">
                            {cleanDepartmentName}
                        </h2>

                        <table className="w-full border-collapse">
                            <thead>
                                <tr className="text-center font-bold underline">
                                    <th className="px-2 py-1">Vendor</th>
                                    <th className="px-2 py-1">Item Name</th>
                                    <th className="px-2 py-1">Brand</th>
                                    <th className="px-2 py-1">UOM</th>
                                    <th className="px-2 py-1">Qty</th>
                                    <th className="px-2 py-1">Rate</th>
                                    <th className="px-2 py-1">Total</th>
                                    {!aggregated && <th className="px-2 py-1">Bill No</th>}
                                </tr>
                            </thead>

                            <tbody>
                                {dept.vendors.map((vendor, vendorIndex) => {
                                    const purchaseItems = vendor.items.filter(i => i.source_type === "purchase");
                                    const debitItems = vendor.items.filter(i => i.source_type === "debitnote");

                                    const purchaseTotal = purchaseItems.reduce((sum, i) => sum + i.Total, 0);
                                    const debitTotal = debitItems.reduce((sum, i) => sum + i.Total, 0);
                                    const netTotal = purchaseTotal - debitTotal;

                                    return (
                                        <React.Fragment key={vendorIndex}>

                                            {/* Vendor Name */}
                                            <tr>
                                                <td className="font-bold capitalize px-2 py-1 border-b-2 border-black text-[15px]">
                                                    {vendor.vendor}
                                                </td>
                                            </tr>

                                            {/* ========= PURCHASE ========= */}
                                            {purchaseItems.map((item, itemIndex) => (
                                                <tr key={`p-${itemIndex}`} className="text-center">
                                                    <td></td>
                                                    <td className="px-2 py-1">{item.Name}</td>
                                                    <td className="px-2 py-1">{item.BrandName || "-"}</td>
                                                    <td className="px-2 py-1">{item.UOM}</td>
                                                    <td className="px-2 py-1">{item.ItemCount}</td>
                                                    <td className="px-2 py-1">{formatSalesValue(item.ItemRate)}</td>
                                                    <td className="px-2 py-1">{formatSalesValue(item.Total)}</td>
                                                    {!aggregated && (
                                                        <td className="px-2 py-1">{item.purchaseBillNumber || "-"}</td>
                                                    )}
                                                </tr>
                                            ))}

                                            {/* Purchase Total */}
                                            <tr>
                                                <td colSpan="6"></td>
                                                <td className="font-bold text-center border-t-2 border-gray-700 pb-1">
                                                    {formatSalesValue(purchaseTotal)}
                                                </td>
                                                {!aggregated && <td></td>}
                                            </tr>

                                            {/* ========= DEBIT NOTES ========= */}
                                            {debitItems.length > 0 && (
                                                <>
                                                    {debitItems.map((item, itemIndex) => (
                                                        <tr key={`d-${itemIndex}`} className="text-center text-red-600 font-medium">
                                                            <td></td>
                                                            <td className="px-2 py-1">{item.Name}</td>
                                                            <td className="px-2 py-1">{item.BrandName || "-"}</td>
                                                            <td className="px-2 py-1">{item.UOM}</td>
                                                            <td className="px-2 py-1">-{item.ItemCount}</td>
                                                            <td className="px-2 py-1">{formatSalesValue(item.ItemRate)}</td>
                                                            <td className="px-2 py-1">-{formatSalesValue(item.Total)}</td>
                                                            {!aggregated && (
                                                                <td className="px-2 py-1">{item.purchaseBillNumber || "-"}</td>
                                                            )}
                                                        </tr>
                                                    ))}

                                                    {/* Debit Total */}
                                                    <tr>
                                                        <td colSpan="6"></td>
                                                        <td className="font-bold text-center border-t-2 border-red-500 text-red-600 pb-2">
                                                            -{formatSalesValue(debitTotal)}
                                                        </td>
                                                        {!aggregated && <td></td>}
                                                    </tr>
                                                </>
                                            )}

                                            {/* ========= NET TOTAL ========= */}
                                            {debitItems.length > 0 && (
                                                <tr>
                                                    <td colSpan="6"></td>
                                                    <td className="font-bold text-center border-t-2 border-black text-green-700 pb-2">
                                                        {formatSalesValue(netTotal)}
                                                    </td>
                                                    {!aggregated && <td></td>}
                                                </tr>
                                            )}

                                        </React.Fragment>
                                    );
                                })}
                            </tbody>
                        </table>

                    </div>
                );
            })}
        </div>
    );
};

export default DepartmentDetailedTable;
